
import React from 'react';
import { motion } from 'framer-motion';
import { AICharacter } from '@/components/legal/types';
import { Sparkles } from 'lucide-react';

interface AICharacterDisplayProps {
  character: AICharacter;
  isSpeaking: boolean;
  size: 'tiny' | 'small' | 'medium' | 'large';
}

const AICharacterDisplay: React.FC<AICharacterDisplayProps> = ({
  character,
  isSpeaking,
  size
}) => {
  // Get size dimensions
  const getSizeClass = () => {
    switch (size) {
      case 'tiny':
        return 'w-6 h-6';
      case 'small':
        return 'w-10 h-10';
      case 'medium':
        return 'w-16 h-16';
      case 'large':
        return 'w-24 h-24';
      default:
        return 'w-10 h-10';
    }
  };

  // Character animation variants
  const characterVariants = {
    idle: {
      y: [0, -2, 0],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: "easeInOut"
      }
    },
    speaking: {
      scale: [1, 1.05, 1, 1.02, 1],
      transition: {
        duration: 0.8,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Generate pixel-art style eyes based on avatar type
  const renderFace = () => {
    if (character.avatarType === 'pixelated') {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {/* Pixelated eyes */}
          <div className="flex space-x-1 mb-1">
            <div className="w-1 h-1 bg-white rounded-sm"></div>
            <div className="w-1 h-1 bg-white rounded-sm"></div>
          </div>
          {/* Pixelated mouth - changes with speaking state */}
          {isSpeaking ? (
            <div className="w-3 h-1 bg-white rounded-sm"></div>
          ) : (
            <div className="w-2 h-0.5 bg-white rounded-sm"></div>
          )}
        </div>
      );
    } else {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {/* Smoother eyes */}
          <div className="flex space-x-1.5 mb-1">
            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
          </div>
          {/* Smoother mouth - changes with speaking state */}
          {isSpeaking ? (
            <div className="w-3 h-1.5 border-2 border-white rounded-full"></div>
          ) : (
            <div className="w-2.5 h-0.5 bg-white rounded-full"></div>
          )}
        </div>
      );
    }
  };

  return (
    <div className={`relative ${getSizeClass()}`}>
      <motion.div
        className={`w-full h-full rounded-full bg-gradient-to-br ${character.color} shadow-inner overflow-hidden ${size === 'tiny' ? 'border border-white/20' : 'border-2 border-white/20'}`}
        variants={characterVariants}
        animate={isSpeaking ? 'speaking' : 'idle'}
      >
        {renderFace()}
      </motion.div>
      
      {isSpeaking && size !== 'tiny' && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute -top-1 -right-1"
        >
          <Sparkles className="h-3 w-3 text-yellow-300" />
        </motion.div>
      )}
    </div>
  );
};

export default AICharacterDisplay;
